/**
 * Google `Polyline` adapter — wraps `google.maps.Polyline` to render a
 * straight-line multi-segment line through ordered points. Added in Phase 4
 * (#325) so LiveTracking's driver-route rendering can sit behind the
 * connector instead of importing `mapbox-gl` directly.
 *
 * **Out of scope: Routes API.** This adapter draws straight lines between
 * consecutive points — no turn-by-turn routing via Google's Directions API.
 * That's a future ticket; until then, the Google connector renders the route
 * as straight segments (visual aid only). The Mapbox adapter renders the
 * same input identically, so the visual is consistent across providers when
 * the caller pre-fetches sampled route points.
 *
 * Implementation: each instance manages its own `google.maps.Polyline` and
 * attaches it to the active map from `useMap`. Mount creates + attaches,
 * unmount detaches + nulls. Prop changes re-set path / options on the live
 * instance so the line updates without remount.
 */

import { useEffect, useRef } from 'react';
import type * as React from 'react';
import { useMap as useGoogleMap, useMapsLibrary } from '@vis.gl/react-google-maps';
import type { PolylineProps } from '../../types';

export const GooglePolyline: React.FC<PolylineProps> = ({
  points,
  color = '#0078d4',
  width = 4,
  opacity = 0.75,
}) => {
  const map = useGoogleMap();
  const mapsLib = useMapsLibrary('maps');
  const polylineRef = useRef<google.maps.Polyline | null>(null);

  // Mount the polyline once the maps library + map are ready. Recreated on
  // map/library change (which essentially only happens on first ready).
  useEffect(() => {
    if (!map || !mapsLib) return;
    const polyline = new mapsLib.Polyline({
      path: points.map((p) => ({ lat: p.lat, lng: p.lng })),
      strokeColor: color,
      strokeWeight: width,
      strokeOpacity: opacity,
      clickable: false,
    });
    polyline.setMap(map);
    polylineRef.current = polyline;
    return () => {
      polyline.setMap(null);
      polylineRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, mapsLib]);

  // Update path on points change — `setPath` is the in-place mutation API,
  // no remount required.
  useEffect(() => {
    const p = polylineRef.current;
    if (!p) return;
    p.setPath(points.map((pt) => ({ lat: pt.lat, lng: pt.lng })));
  }, [points]);

  // Update stroke options on style change.
  useEffect(() => {
    const p = polylineRef.current;
    if (!p) return;
    p.setOptions({
      strokeColor: color,
      strokeWeight: width,
      strokeOpacity: opacity,
    });
  }, [color, width, opacity]);

  return null;
};
